import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import SEOHead from '../components/SEOHead';
import PublicPageShell from '../components/landing/PublicPageShell';
import { Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactPage = () => {
  const { t, language } = useLanguage();
  const isFr = language !== 'en';

  const [form, setForm] = useState({ name: '', email: '', company_name: '', phone: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSending(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/contact/demo-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Erreur');
      }
      setSent(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  const facts = [
    { label: t('email'), value: 'contact@fretmanager.com' },
    { label: t('phone'), value: '+33 1 23 45 67 89' },
    { label: t('addressLabel'), value: 'Paris, France' },
    { label: t('hours'), value: t('hoursValue') },
  ];

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  return (
    <PublicPageShell
      title={t('contactUs')}
      lede={isFr
        ? 'Présentez-nous vos routes, vos volumes et votre organisation actuelle. Nous revenons vers vous sous un jour ouvré.'
        : 'Tell us about your routes, volumes and current setup. We get back to you within one business day.'}
    >
      <SEOHead
        title={t('seoContactTitle')}
        description={t('seoContactDesc')}
        canonical="/contact"
      />

      <div className="landing-contact-grid">
        <div>
          <h2 className="landing-card-title">
            {isFr ? 'Nous joindre' : 'Reach us'}
          </h2>
          <ul className="landing-facts">
            {facts.map(({ label, value }) => (
              <li key={label}>
                <span className="landing-fact-label">{label}</span>
                <span className="landing-fact-value">{value}</span>
              </li>
            ))}
          </ul>
          <p className="landing-form-note" style={{ marginTop: '1.25rem' }}>
            {isFr
              ? 'Une démonstration dure une trentaine de minutes et se fait sur vos propres cas.'
              : 'A demo takes about thirty minutes and runs on your own cases.'}
          </p>
        </div>

        <div className="landing-card">
          {sent ? (
            <div className="landing-form-success">
              <span className="landing-form-success-mark" aria-hidden="true">
                <Check size={18} />
              </span>
              <div>
                <h3>{t('messageSent')}</h3>
                <p>{t('willReplyIn24h')}</p>
              </div>
            </div>
          ) : (
            <>
              <h2 className="landing-card-title">{t('sendUsMessage')}</h2>

              {error && <p className="landing-form-error">{error}</p>}

              <form onSubmit={handleSubmit} noValidate={false}>
                <div className="landing-form-grid">
                  <div className="landing-field">
                    <label htmlFor="contact-name">{isFr ? 'Nom complet' : 'Full name'}</label>
                    <input id="contact-name" required value={form.name} onChange={set('name')} autoComplete="name" />
                  </div>
                  <div className="landing-field">
                    <label htmlFor="contact-email">{t('email')}</label>
                    <input id="contact-email" type="email" required value={form.email} onChange={set('email')} autoComplete="email" />
                  </div>
                  <div className="landing-field">
                    <label htmlFor="contact-company">{isFr ? 'Entreprise' : 'Company'}</label>
                    <input id="contact-company" required value={form.company_name} onChange={set('company_name')} autoComplete="organization" />
                  </div>
                  <div className="landing-field">
                    <label htmlFor="contact-phone">
                      {t('phone')} <span>({isFr ? 'facultatif' : 'optional'})</span>
                    </label>
                    <input id="contact-phone" value={form.phone} onChange={set('phone')} autoComplete="tel" />
                  </div>
                  <div className="landing-field landing-field--full">
                    <label htmlFor="contact-message">
                      {isFr ? 'Votre message' : 'Your message'} <span>({isFr ? 'facultatif' : 'optional'})</span>
                    </label>
                    <textarea
                      id="contact-message"
                      value={form.message}
                      onChange={set('message')}
                      placeholder={isFr
                        ? 'Pays desservis, volumes mensuels, outils actuels…'
                        : 'Countries served, monthly volumes, current tools…'}
                    />
                  </div>
                </div>

                <div className="landing-form-footer">
                  <p className="landing-form-note">
                    {isFr
                      ? 'Vos coordonnées servent uniquement à répondre à cette demande.'
                      : 'Your details are used only to answer this request.'}
                  </p>
                  <button type="submit" className="btn-primary" disabled={sending}>
                    {sending ? t('sending') : t('send')}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </PublicPageShell>
  );
};

export default ContactPage;
